import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableSortLabel,
  TableContainer,
  LinearProgress,
  TablePagination,
  Button,
  IconButton
} from '@material-ui/core';
import { Close, Group as GroupIcon } from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { useFindUsers } from '../../../user/User.hooks';
import { Role, User } from '../../../user/User.interface';
import GroupUserAssignDialog from '../dialogs/GroupUserAssignDialog';
import { useRemoveUsersFromGroup } from '../Group.hooks';
import { roles as roleLabels } from '../../../user/User.interface';

const StyledLoadingTableCell = styled(TableCell)`
  padding: 16px 0 0 !important;
`;

const StyleDiv = styled.div`
  display: flex;
  justify-content: 'center';
`;

export interface GroupUsersCardProps {
  groupId: number;
  fetchRef?: React.MutableRefObject<Function | undefined>;
}

const GroupUsersCard: React.FC<GroupUsersCardProps> = (
  props: GroupUsersCardProps
) => {
  const { groupId, fetchRef } = props;
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [limit, setLimit] = useQueryParam('limit', NumberParam);
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    StringParam
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);
  const [forRemoval, setForRemoval] = useState<User>();
  const { userList, isLoading, error, get } = useFindUsers();
  const defaultLimit: number = 100;
  const users: User[] | undefined = userList?.data;
  const hasMembers: boolean = Boolean(users && users.length > 0);
  const {
    data,
    isLoading: isDeleting,
    removeUsersFromGroup
  } = useRemoveUsersFromGroup(groupId);

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const findUsersWithParams = useCallback(() => {
    const nLimit: number = Number(limit);
    const params = {
      groupId,
      withRoles: true,
      page: Number(page) || 0,
      limit: isFinite(nLimit) ? nLimit : defaultLimit,
      order: String(order || '')
    } as any;
    if (order && orderDirection) {
      params.order = `${order} ${orderDirection}`;
    }
    get(params);
  }, [get, groupId, page, limit, order, orderDirection]);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findUsersWithParams;
    }
    findUsersWithParams();
  }, [fetchRef, findUsersWithParams]);

  useEffect(() => {
    if (data && !isDeleting) {
      setOpenConfirmDelete(false);
      setForRemoval(undefined);
      findUsersWithParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isDeleting]);

  return (
    <Card>
      <CardTitle style={{ flex: 1, justifyContent: 'space-between' }}>
        <StyleDiv>
          <GroupIcon />
          Team Members
        </StyleDiv>
        <Button
          variant='contained'
          color='primary'
          onClick={() => setOpenDialog(true)}
        >
          Add Employee
        </Button>
      </CardTitle>
      <TableContainer>
        <Table>
          {!isLoading && hasMembers && (
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'firstName'}
                    direction={getSortDirection('firstName')}
                    onClick={() => handleSort('firstName')}
                  >
                    First Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'lastName'}
                    direction={getSortDirection('lastName')}
                    onClick={() => handleSort('lastName')}
                  >
                    Last Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'email'}
                    direction={getSortDirection('email')}
                    onClick={() => handleSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>Role/s</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {isLoading && (
              <TableRow>
                <StyledLoadingTableCell colSpan={4}>
                  <LinearProgress />
                </StyledLoadingTableCell>
              </TableRow>
            )}
            {!isLoading && !hasMembers && (
              <TableRow>
                <TableCell colSpan={4}>
                  {error?.message || 'No team members to show.'}
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              hasMembers &&
              users!.map((user: User) => (
                <TableRow
                  hover
                  key={user.id}
                  onClick={() => history.push(`/portal/users/${user.id}`)}
                >
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.roles
                      ?.map(
                        (role: Role) =>
                          roleLabels.find(rl => rl.name === role.name)?.label ||
                          role.name
                      )
                      .join(', ') || ''}
                  </TableCell>
                  <TableCell align='right'>
                    <IconButton
                      size='small'
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        setForRemoval(user);
                        setOpenConfirmDelete(true);
                      }}
                    >
                      <Close />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={userList?.total || 0}
        rowsPerPage={userList?.limit || defaultLimit}
        page={userList?.page ? userList?.page - 1 : 0}
        onPageChange={(_, newPage: number) => setPage(newPage)}
        onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      {openDialog && (
        <GroupUserAssignDialog
          open={Boolean(openDialog)}
          groupId={groupId}
          onCancel={() => setOpenDialog(false)}
          onAssigned={() => {
            findUsersWithParams();
            setOpenDialog(false);
          }}
        />
      )}
      {openConfirmDelete && (
        <ConfirmDialog
          open={openConfirmDelete}
          title='Confirm Removal'
          message={`Are you sure you want to remove ${forRemoval?.firstName} ${forRemoval?.lastName} from the team ?`}
          confirmLabel='Remove'
          onCancel={() => {
            setOpenConfirmDelete(false);
            setForRemoval(undefined);
          }}
          onConfirm={() => {
            setOpenConfirmDelete(false);
          }}
        />
      )}
      {!openConfirmDelete && forRemoval && (
        <ConfirmDialog
          open
          title='Update User Roles'
          message='Would you like to update the user roles with these new settings?'
          cancelLabel='No'
          confirmLabel={isDeleting ? 'Removing...' : 'Yes'}
          onCancel={() => {
            if (forRemoval) {
              removeUsersFromGroup(forRemoval.id, false);
            }
          }}
          onConfirm={() => {
            if (forRemoval) {
              removeUsersFromGroup(forRemoval.id, true);
            }
          }}
        />
      )}
    </Card>
  );
};

export default GroupUsersCard;
